import { NavLink, Outlet, useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ImageIcon,
  LayoutGrid,
  CalendarClock,
  Tv,
  LogOut,
  MonitorPlay,
  Building2,
  Users,
  Clock,
  AlertTriangle,
  MessageCircle,
  CreditCard,
} from "lucide-react";

const baseItems = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, tid: "nav-overview" },
  { to: "/admin/media", label: "Media", icon: ImageIcon, tid: "nav-media" },
  { to: "/admin/layouts", label: "Layouts", icon: LayoutGrid, tid: "nav-layouts" },
  { to: "/admin/schedules", label: "Schedules", icon: CalendarClock, tid: "nav-schedules" },
  { to: "/admin/devices", label: "Devices", icon: Tv, tid: "nav-devices" },
  { to: "/admin/bot", label: "Bot Assistant", icon: MessageCircle, tid: "nav-bot" },
  { to: "/admin/users", label: "Users", icon: Users, tid: "nav-users" },
];

function TrialBanner() {
  const [info, setInfo] = useState(null);
  const [show, setShow] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/trial-info").then(({ data }) => {
      setInfo(data);
    }).catch(() => {});
  }, []);

  if (!info?.is_trial || !show) return null;

  const days = info.remaining_days;
  const expired = info.expired;
  const nearExpiry = !expired && days <= 2;

  // Sudah expired — tampilkan notice, user klik sendiri
  if (expired) {
    return (
      <div className="px-6 py-3 flex items-center justify-between gap-3 text-sm bg-red-50 border-b border-red-200">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <span className="text-red-800">
            <strong>❌ Trial Expired</strong> — Silakan perpanjang untuk melanjutkan.
          </span>
        </div>
        <Link
          to="/harga"
          className="text-xs font-bold text-red-700 bg-red-100 px-3 py-1.5 hover:bg-red-200 transition-colors"
        >
          Lihat paket harga
        </Link>
      </div>
    );
  }

  return (
    <div className={`px-6 py-3 flex items-center justify-between gap-3 text-sm ${
      nearExpiry ? "bg-amber-50 border-b border-amber-200" : "bg-indigo-50 border-b border-indigo-200"
    }`}>
      <div className="flex items-center gap-2">
        {nearExpiry ? (
          <AlertTriangle className="h-4 w-4 text-amber-600" />
        ) : (
          <Clock className="h-4 w-4 text-indigo-600" />
        )}
        <span className={nearExpiry ? "text-amber-800" : "text-indigo-800"}>
          Trial akun Anda aktif —{" "}
          <strong>{days === 0 ? "hari terakhir" : days === 1 ? "1 hari lagi" : `${days} hari lagi`}</strong>
        </span>
      </div>
      <div className="flex items-center gap-3">
        <Link
          to="/payment"
          className={`text-xs font-semibold underline ${
            nearExpiry ? "text-amber-700" : "text-indigo-600"
          }`}
        >
          {nearExpiry ? "Perpanjang Sekarang →" : "Lihat Paket →"}
        </Link>
        <button onClick={() => setShow(false)} className="text-zinc-400 hover:text-zinc-600 text-xs">
          ✕
        </button>
      </div>
    </div>
  );
}


export default function AdminShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";
  const subItem = { to: "/harga", label: "Langganan", icon: CreditCard, tid: "nav-subscription" };
  const items = isSuperAdmin
    ? [...baseItems, subItem,
      { to: "/admin/properties", label: "Properties", icon: Building2, tid: "nav-properties" }]
    : [...baseItems, subItem];

  return (
    <div className="min-h-screen flex bg-[#F4F4F5]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-200 bg-white h-screen sticky top-0 flex flex-col" data-testid="sidebar">
        <div className="h-16 px-6 flex items-center gap-2 border-b border-zinc-200">
          <img src="/logo.svg" alt="DigiSign" className="h-8" />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map(({ to, label, icon: Icon, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-[#002FA7] bg-zinc-50 text-zinc-900 font-medium"
                    : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        {user?.property && (
          <div className="px-4 py-2 mx-3 mb-1 bg-[#002FA7]/5 border border-[#002FA7]/10 rounded">
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#002FA7]/60">Property</p>
            <p className="text-sm font-semibold text-[#002FA7] truncate">{user.property.name}</p>
          </div>
        )}
        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || "Admin"}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/login"); }}
              className="p-2 hover:bg-zinc-100"
              title="Log out"
            >
              <LogOut className="h-4 w-4 text-zinc-600" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <TrialBanner />
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
